import React, { useMemo, useState } from 'react';
import cn from 'classnames';

import { DynamicIconWrapper, Icons, Modal, SpinnerOverlay } from '@components/common';
import CategoriesList from '@components/common/CategoriesList';
import TextAvatar from '@components/common/TextAvatar/TextAvatar';
import ProfileUserForm from '@components/ModalComponents/ProfileUserForm';

import { useQueryUserProfile } from '@hooks/userProfile/useQueryUserProfile';

import { formatPhoneNumber } from '@utils/phoneUtils';
import { prepareUserRolesForUserProfile } from '@utils/rolesUtils';

import { ITitle } from '@interfaces/common/common';

import { EUserRoles, TUserRoles } from '@enums/common';

import { MODAL_WIDTH_580 } from '@constants/modals';

import OrganisationShortInfo from './OrganisationShortInfo';

import styles from './UserProfile.module.scss';

const UserProfile = () => {
	const { userProfile, isLoadingUserProfile } = useQueryUserProfile();

	const [isOpenOrganisationShortInfo, setIsOpenOrganisationShortInfo] = useState(false);
	const [isOpenEditUserProfileModal, setIsOpenEditUserProfileModal] = useState(false);
	const [isDirty, setIsDirty] = useState(false);

	const toggleOrganisationShortInfo = () => setIsOpenOrganisationShortInfo(prevState => !prevState);

	const openEditUserProfileModal = () => setIsOpenEditUserProfileModal(true);

	const closeEditUserProfileModal = () => setIsOpenEditUserProfileModal(false);

	const fullName = `${userProfile?.firstName} ${userProfile?.lastName}`;

	const isRequiredJobTitle = useMemo(() => userProfile?.roles.reduce<Array<TUserRoles>>((acc, { roles }) => {
			const actualRoles = roles.filter(({ isRevoked }) => !isRevoked).map(({ role }) => role.name);

			return [...acc, ...actualRoles];
		}, []).includes(EUserRoles.assetOwnerContact),
		[userProfile?.roles]);

	return (
		<div className={styles.userProfileContainer}>
			{userProfile && (
				<>
					<Modal
						isOpen={isOpenOrganisationShortInfo}
						isTitleHidden
						onClose={toggleOrganisationShortInfo}
						width={MODAL_WIDTH_580}
					>
						<OrganisationShortInfo userProfile={userProfile} />
					</Modal>
					<Modal
						isConfirmDialogNeededOnClose
						isDirtyForm={isDirty}
						isOpen={isOpenEditUserProfileModal}
						onClose={closeEditUserProfileModal}
						title='Edit profile'
						width='770px'
					>
						<ProfileUserForm
							closeEditUserProfileModal={closeEditUserProfileModal}
							isRequiredJobTitle={!!isRequiredJobTitle}
							onClose={closeEditUserProfileModal}
							setIsDirty={setIsDirty}
							userProfile={userProfile}
						/>
					</Modal>
				</>
			)}

			<div className={styles.userProfileContent}>
				{isLoadingUserProfile && <SpinnerOverlay />}
				<div className={styles.mainInfoSection}>
					<div className={cn(styles.whiteWrapper, styles.nameSection)}>
						<TextAvatar
							avatarId={userProfile?.avatar?.id}
							firstName={userProfile?.firstName}
							fontSize='20px'
							id={userProfile?.id}
							isProfileLogo
							lastName={userProfile?.lastName}
							size='75px'
						/>
						<div>
							<div className={styles.fullName}>{fullName}</div>
							{!!userProfile?.jobTitle
								&& <div className={styles.jobTitle}>
									{userProfile?.jobTitle}
								</div>
							}
							<div className={styles.email}>
								{Icons.emailIcon()}
								{userProfile?.email}
							</div>
							{userProfile?.phoneNumber
								&& <div className={styles.phone}>
									{Icons.phoneIcon()}
									{formatPhoneNumber(userProfile.phoneNumber)}
								</div>}
						</div>
						<div className={styles.editIcon}>
							<DynamicIconWrapper
								icon={Icons.editIcon}
								onClick={openEditUserProfileModal}
								tooltipLabel='Edit profile'
							/>
						</div>
					</div>
					<div className={cn(styles.whiteWrapper, styles.statusSection)}>
						<div className={styles.orgAvatarWrapper}>
							<div>
								<TextAvatar
									avatarId={userProfile?.organization?.avatar?.id}
									className={styles.avatar}
									fontSize='12px'
									fullName={userProfile?.organization.name}
									id={userProfile?.organization.id}
									size='24px'
								/>
							</div>
							<div className={styles.name}>{userProfile?.organization.name}</div>
						</div>
						<div className={styles.organisationType}>
							{userProfile?.organization.types.map(({ title }: ITitle) => title).join(', ')}
						</div>
						<div>
							<div className={styles.country}>
								{userProfile?.organization.country.name}
								{!!userProfile?.organization?.city && `, ${userProfile.organization.city}`}
							</div>
							<div className={styles.postcode}>
								{userProfile?.organization.postcode}
							</div>
						</div>
						{!!userProfile &&
							<div
								className={styles.info}
								onClick={toggleOrganisationShortInfo}
							>
								<DynamicIconWrapper icon={Icons.info} />
							</div>
						}
					</div>
				</div>
				<div className={cn(styles.whiteWrapper, styles.profileSectionWrapper)}>
					<div className={styles.profileSection}>
						<div className={styles.sectionName}>Roles</div>
						<div className={styles.rolesWrapper}>
							{!!userProfile?.roles.length && userProfile.roles.map(({ header, roles }) => {
								const listOfRoles = prepareUserRolesForUserProfile(roles);

								return (
									<div key={`${userProfile.id}${header}`}>
										<div className={styles.categoryName}>{header}</div>
										<CategoriesList
											categories={listOfRoles}
											disableTooltip='This role has been revoked'
										/>
									</div>
								);
							})}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default UserProfile;
